import { RIDES, type RideId } from "./sim/rides";
import { DEFAULT_SPECIAL_SEQS, POP_VARIANTS, type Dir, type SpecialSeqs, type SpecialTrick } from "./tricks";

// Customizable special sequences (#135): which two stick taps pull each
// special. Sim-free; Settings saves them, the Specials screen edits them and
// the skater reads them through `specialTrick`.

/**
 * Every special the player can remap, with the ride it's pulled on: the
 * board's, then the bike's (#138). Names are unique across rides, so one
 * `SpecialSeqs` holds them all; a pair only clashes with another special on
 * the same ride and button.
 */
export const RIDE_SPECIALS: readonly { ride: RideId; trick: SpecialTrick }[] = (Object.keys(RIDES) as RideId[]).flatMap((ride) =>
  RIDES[ride].tricks.specials.map((trick) => ({ ride, trick })),
);

const rideSpecial = (name: string) => RIDE_SPECIALS.find((s) => s.trick.name === name);

export type Tap = Exclude<Dir, "n">;

/**
 * Every pair a special can be set to, in the order ←/→ steps through them:
 * the ones with a down first (up, left, right then down; down then up, left,
 * right), then the rest. Up-up and down-down aren't here: they're the
 * Boneless and Fastplant pops.
 */
export const SPECIAL_SEQ_CHOICES: readonly (readonly [Tap, Tap])[] = (() => {
  const others: Tap[] = ["u", "l", "r"];
  const down = [...others.map((t): [Tap, Tap] => [t, "d"]), ...others.map((t): [Tap, Tap] => ["d", t])];
  const rest: [Tap, Tap][] = [];
  for (const a of others) for (const b of others) if (!(a === "u" && b === "u")) rest.push([a, b]);
  return [...down, ...rest];
})();

const ARROWS: Record<Dir, string> = { u: "↑", d: "↓", l: "←", r: "→", n: "·" };

/** "↑": one tap as a stick arrow. */
export function tapLabel(d: Dir): string {
  return ARROWS[d];
}

/** "↑ ↓": a sequence as stick arrows. */
export function seqLabel(seq: readonly [Dir, Dir]): string {
  return `${ARROWS[seq[0]]} ${ARROWS[seq[1]]}`;
}

/** A special's sequence on these settings (its default if they don't say). */
export function seqOf(seqs: SpecialSeqs, s: SpecialTrick): readonly [Dir, Dir] {
  return seqs[s.name] ?? s.seq;
}

const same = (a: readonly [Dir, Dir], b: readonly [Dir, Dir]) => a[0] === b[0] && a[1] === b[1];

/**
 * Why `name` can't have `seq`, or null if it can: a pop's taps, or another
 * special on the same button already has it (named, so the menu can say).
 */
export function specialSeqProblem(seqs: SpecialSeqs, name: string, seq: readonly [Dir, Dir]): string | null {
  const pop = POP_VARIANTS.find((v) => same(v.seq, seq));
  if (pop) return `${seqLabel(seq)} is the ${pop.name}`;
  if (!SPECIAL_SEQ_CHOICES.some((c) => same(c, seq))) return `${seqLabel(seq)} can't be a special`;
  const me = rideSpecial(name);
  const other = RIDE_SPECIALS.find(
    ({ ride, trick: s }) => s.name !== name && ride === me?.ride && s.button === me.trick.button && same(seqOf(seqs, s), seq),
  );
  return other ? `${seqLabel(seq)} is already the ${other.trick.name}` : null;
}

/** These sequences with `name` set to `seq` (checked with `specialSeqProblem` first). */
export function setSpecialSeq(seqs: SpecialSeqs, name: string, seq: readonly [Dir, Dir]): SpecialSeqs {
  return { ...seqs, [name]: [seq[0], seq[1]] };
}

/** `name` stepped to the next (or previous) free choice in `SPECIAL_SEQ_CHOICES`. */
export function stepSpecialSeq(seqs: SpecialSeqs, name: string, dir: -1 | 1): SpecialSeqs {
  const s = rideSpecial(name)?.trick;
  if (!s) return seqs;
  const n = SPECIAL_SEQ_CHOICES.length;
  const at = SPECIAL_SEQ_CHOICES.findIndex((c) => same(c, seqOf(seqs, s)));
  for (let i = 1; i < n; i++) {
    const next = SPECIAL_SEQ_CHOICES[(((at < 0 ? 0 : at) + dir * i) % n + n) % n]!;
    if (!specialSeqProblem(seqs, name, next)) return setSpecialSeq(seqs, name, next);
  }
  return seqs;
}

/** True when every special is on its default. */
export function defaultSpecials(seqs: SpecialSeqs): boolean {
  return RIDE_SPECIALS.every(({ trick: s }) => same(seqOf(seqs, s), s.seq));
}

/**
 * Saved sequences, parsed defensively: each special's pair if it's a valid
 * choice, else its default; and if that leaves two specials on one ride's
 * button sharing a pair, all the defaults.
 */
export function parseSpecialSeqs(json: unknown): SpecialSeqs {
  if (!json || typeof json !== "object") return DEFAULT_SPECIAL_SEQS;
  const o = json as Record<string, unknown>;
  const out: Record<string, readonly [Dir, Dir]> = { ...DEFAULT_SPECIAL_SEQS };
  for (const { trick: s } of RIDE_SPECIALS) {
    const v = o[s.name];
    if (Array.isArray(v) && v.length === 2) {
      const choice = SPECIAL_SEQ_CHOICES.find((c) => c[0] === v[0] && c[1] === v[1]);
      if (choice) out[s.name] = [choice[0], choice[1]];
    }
  }
  const keys = RIDE_SPECIALS.map(({ ride, trick: s }) => `${ride}:${s.button}:${seqOf(out, s).join("")}`);
  return new Set(keys).size === keys.length ? out : DEFAULT_SPECIAL_SEQS;
}
